import DateUtils from "./DateUtils";
import { RegistrationStates } from "../config/RegistrationConfigs";
import { MarketAccessTypes, MarketReasonCodeLabels, hasUnexpiredBuyerPayingLock } from "../config/MarketConfigs";

/**
 * Determine whether the authenticated buyer is eligible to purchase from this race's marketplace
 * client-side. The backend re-validates on /buyOffer; this is for UX (disable CTA / gate navigation).
 *
 * @param {Object} race
 * @param {Object|null} myRegistration
 * @param {{ isAdmin: boolean, isAuthority: boolean }} userPermissions
 * @param {Record<string, boolean>|undefined} masterStatus - from `/app/load` (`race.series` key → masters list membership)
 * @returns {{disabled: boolean, reason?: string}}
 */
export function deriveBuyerEligibility(race, myRegistration, userPermissions, masterStatus) {
  if (!race?.hasMarket) {
    return { disabled: true, reason: "Tržiště pro tento závod není povolené." };
  }
  if (!DateUtils.isWithinInterval(race.market?.interval)) {
    return { disabled: true, reason: "Nákupy jsou momentálně uzavřené." };
  }

  const purchaseAccess = race.market?.purchaseAccess || MarketAccessTypes.PUBLIC;
  if (purchaseAccess === MarketAccessTypes.MASTERS_AND_UNDRAWN) {
    const isUndrawn = myRegistration?.state === RegistrationStates.UNDRAWN;
    const isUnpaidPostLottery = myRegistration?.state === RegistrationStates.UNPAID;
    const series = race.series;
    const isMasterForRaceSeries =
      typeof series === "string" &&
      typeof masterStatus === "object" &&
      masterStatus !== null &&
      masterStatus[series] === true;

    if (
      !userPermissions?.isAdmin &&
      !userPermissions?.isAuthority &&
      !isUndrawn &&
      !isUnpaidPostLottery &&
      !isMasterForRaceSeries
    ) {
      return {
        disabled: true,
        reason: "Tento závod má omezený přístup – kupovat mohou Masters, nevylosovaní a nezaplacení po losování.",
      };
    }
  }

  if (myRegistration) {
    const blockingStates = new Set([
      RegistrationStates.ON_START_LIST,
      RegistrationStates.PREREGISTERED,
      RegistrationStates.CONFIRMED,
      RegistrationStates.OVERDUE,
      RegistrationStates.UNPAID_PREREG_FEE,
      RegistrationStates.PENDING_MARKET,
      RegistrationStates.SOLD,
      RegistrationStates.MARKET_PENDING_DATA,
    ]);
    if (blockingStates.has(myRegistration.state)) {
      if (myRegistration.state === RegistrationStates.PENDING_MARKET) {
        return {
          disabled: true,
          reason:
            "Máte na tomto závodě vlastní aktivní nabídku na tržišti – dokud ji nestáhnete nebo se váš slot neprodá, nemůžete zároveň kupovat od jiného běžce. Spravujte ji výše v sekci „Moje nabídky“.",
        };
      }
      if (myRegistration.state === RegistrationStates.MARKET_PENDING_DATA) {
        return {
          disabled: true,
          reason:
            "Nejdřív dokončete registraci po nákupu slotu na tržišti. Poté vám systém znovu vyhodnotí, co smíte na tržišti dělat.",
        };
      }
      return {
        disabled: true,
        reason: "Stav vaší stávající registrace nedovoluje koupit slot na tržišti.",
      };
    }
  }

  return { disabled: false };
}

/**
 * Buyer eligibility for **starting a purchase** (offer buy CTA on the marketplace page), including
 * an unexpired PAYING lock on any offer (`/market/myPurchases`), matching backend `BUYER_ALREADY_LOCKED`.
 * Not used for dashboard navigation links — use {@link deriveBuyerEligibility} there.
 *
 * @param {Object} race
 * @param {Object|null} myRegistration
 * @param {{ isAdmin: boolean, isAuthority: boolean }} userPermissions
 * @param {Record<string, boolean>|undefined} masterStatus
 * @param {Array<{ state?: string, lockExpiresAt?: string|Date }>|null|undefined} myPurchaseOffers
 * @returns {{disabled: boolean, reason?: string}}
 */
export function deriveAuthenticatedBuyerEligibility(
  race,
  myRegistration,
  userPermissions,
  masterStatus,
  myPurchaseOffers,
) {
  if (hasUnexpiredBuyerPayingLock(myPurchaseOffers)) {
    return { disabled: true, reason: MarketReasonCodeLabels.BUYER_ALREADY_LOCKED };
  }
  return deriveBuyerEligibility(race, myRegistration, userPermissions, masterStatus);
}
